import { produce } from 'immer'
import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { createStore, useStore, type StateCreator } from 'zustand'
import { persist } from 'zustand/middleware'
import { useGetScanFindings, type ScanFindingsFilters } from '../../utils/api-client/api-hooks'
import {
  SeverityUpdateFilterSchema,
  type Confidence,
  type FindingAggregateResponse,
  type FindingAggregateResponseStatistics,
  type FindingEmbedParameter,
  type FindingSortField,
  type ScaClassificationResponse,
  type SeverityUpdateFilter,
  type SortOrder,
  type TriageSuggestedStatusResponse,
} from '../../utils/api-client/api-schemas'
import { Pagination } from '../table/table'

export type FilterableClassification = TriageSuggestedStatusResponse

type FixConfidence = Confidence

export type FilterableFixStatus = 'completed' | 'failed' | 'no-recommendations-available' | 'blocked' | 'excluded'
export type FilterablePatchStatus = 'issued' | 'merged'

export type FilterStateStoreValues = {
  hasFix: boolean
  hasTriage: boolean
  hasScaAnalysis: boolean
  /**
   * When true, filters to findings WITHOUT any analysis (triage or fix).
   * Used by the "unanalyzed" quick view for SAST scans.
   * Sends `analyzed: false` to the API.
   */
  filterToUnanalyzed?: boolean
  /**
   * When true, filters to ONLY fully analyzed findings.
   * Used by the "analyzed" quick view for SAST scans.
   * Sends `analyzed: true` to the API.
   */
  filterToAnalyzed?: boolean
  /**
   * When true, filters to findings WITHOUT SCA analysis.
   * Used by the "sca-unanalyzed" quick view for SCA scans.
   * Sends `sca-status: not_completed` to the API.
   */
  filterToScaUnanalyzed?: boolean
  classification: Record<FilterableClassification, boolean>
  fixResult: Record<FilterableFixStatus, boolean>
  patchStatus: Record<FilterablePatchStatus, boolean>
  fixConfidence: Record<FixConfidence, boolean>
  scaClassification: Record<ScaClassificationResponse, boolean>
  severityUpdate: Record<SeverityUpdateFilter, boolean>
  severity: {
    labels: Record<string, boolean>
    minScore: number | null
    maxScore: number | null
  }
  suggestedSeverity: {
    labels: Record<string, boolean>
    minScore: number | null
    maxScore: number | null
  }
}

export type FilterStateStore = FilterStateStoreValues & {
  resetFilters: () => unknown
  toggleHasFix: () => unknown
  toggleHasTriage: () => unknown
  toggleHasScaAnalysis: () => unknown
  toggleClassification: (key: FilterableClassification) => unknown
  toggleClassificationGroup: () => unknown
  toggleFixResult: (key: FilterableFixStatus) => unknown
  toggleFixResultGroup: () => unknown
  togglePatchStatus: (key: FilterablePatchStatus) => unknown
  togglePatchStatusGroup: () => unknown
  toggleFixConfidence: (key: string) => unknown
  toggleFixConfidenceGroup: () => unknown
  toggleScaClassification: (key: string) => unknown
  toggleScaClassificationGroup: () => unknown
  toggleNoFixNecessary: () => unknown
  toggleFixResultBlocked: () => unknown
  toggleSeverityUpdate: (key: SeverityUpdateFilter) => unknown
  toggleSeverityUpdateGroup: () => unknown
  toggleSeverityLabel: (label: string) => unknown
  /**
   * Selects every label in `availableLabels` when none is selected, and clears the selection
   * otherwise. The scale lives in the component tree rather than the store, so the caller supplies
   * the labels it is rendering.
   */
  toggleSeverityGroup: (availableLabels: string[]) => unknown
  setSeverityMinScore: (value: number | null) => unknown
  setSeverityMaxScore: (value: number | null) => unknown
  toggleSuggestedSeverityLabel: (label: string) => unknown
  /** @see toggleSeverityGroup */
  toggleSuggestedSeverityGroup: (availableLabels: string[]) => unknown
  setSuggestedSeverityMinScore: (value: number | null) => unknown
  setSuggestedSeverityMaxScore: (value: number | null) => unknown
  toggleAnalyzed: () => unknown
  isInitialState: () => boolean
}

export const DEFAULT_INITIAL_STATE = {
  hasFix: false,
  hasTriage: false,
  hasScaAnalysis: false,
  filterToAnalyzed: false,
  filterToUnanalyzed: false,
  filterToScaUnanalyzed: false,
  classification: {
    true_positive: false,
    suspicious: false,
    false_positive: false,
    wont_fix: false,
    inconclusive: false,
    blocked: false,
    excluded: false,
    error: false,
    not_triaged: false,
  },
  fixResult: {
    completed: false,
    failed: false,
    'no-recommendations-available': false,
    blocked: false,
    excluded: false,
  },
  patchStatus: {
    issued: false,
    merged: false,
  },
  fixConfidence: {
    high: false,
    medium: false,
    low: false,
    'no-rating': false,
  },
  scaClassification: {
    exploitable: false,
    'not-exploitable': false,
    inconclusive: false,
    'potentially-exploitable': false,
  },
  severityUpdate: {
    decreased: false,
    increased: false,
    no_update: false,
  },
  severity: {
    labels: {},
    minScore: null,
    maxScore: null,
  },
  suggestedSeverity: {
    labels: {},
    minScore: null,
    maxScore: null,
  },
} satisfies FilterStateStoreValues

export type FilterCategory = 'classification' | 'fixResult' | 'fixConfidence' | 'scaClassification'

export type PerCategoryStatistics = Partial<Record<FilterCategory, FindingAggregateResponseStatistics>>

/**
 * Creates a new filter state with the given category's filters zeroed out.
 * Used to compute per-category statistics: each category gets stats from a query
 * that excludes that category's own filters, so intra-category counts stay unfiltered
 * while cross-category counts reflect active filters.
 */
export function createFilterStateWithoutCategory(
  filterState: FilterStateStoreValues,
  category: FilterCategory
): FilterStateStoreValues {
  switch (category) {
    case 'classification':
      return { ...filterState, classification: DEFAULT_INITIAL_STATE.classification }
    case 'fixResult':
      return {
        ...filterState,
        fixResult: DEFAULT_INITIAL_STATE.fixResult,
        patchStatus: DEFAULT_INITIAL_STATE.patchStatus,
      }
    case 'fixConfidence':
      return { ...filterState, fixConfidence: DEFAULT_INITIAL_STATE.fixConfidence }
    case 'scaClassification':
      return { ...filterState, scaClassification: DEFAULT_INITIAL_STATE.scaClassification }
  }
}

/**
 * Checks whether a filter category has any active filters in the given state.
 */
export function categoryHasActiveFilters(filterState: FilterStateStoreValues, category: FilterCategory): boolean {
  switch (category) {
    case 'classification':
      return Object.values(filterState.classification).some(Boolean)
    case 'fixResult':
      return Object.values(filterState.fixResult).some(Boolean) || Object.values(filterState.patchStatus).some(Boolean)
    case 'fixConfidence':
      return Object.values(filterState.fixConfidence).some(Boolean)
    case 'scaClassification':
      return Object.values(filterState.scaClassification).some(Boolean)
  }
}

function areBooleanRecordsEqual<TKey extends string>(a: Record<TKey, boolean>, b: Record<TKey, boolean>) {
  return !Object.keys(a).some(key => a[key] !== b[key])
}

/**
 * Clears the selection when anything is selected, and selects every available label otherwise.
 *
 * Label records only ever hold selected labels — the per-label toggle deletes the key when turning
 * one off — so "select all" has to come from the caller's label list rather than the record's keys.
 */
function toggledLabelGroup(current: Record<string, boolean>, availableLabels: string[]): Record<string, boolean> {
  if (Object.values(current).some(value => value)) {
    return {}
  }
  return Object.fromEntries(availableLabels.map(label => [label, true]))
}

export function getAdditionalFilterCount(current: FilterStateStoreValues, initial: FilterStateStoreValues): number {
  let count = 0
  if (!areBooleanRecordsEqual(current.classification, initial.classification)) count++
  if (!areBooleanRecordsEqual(current.fixResult, initial.fixResult)) count++
  if (!areBooleanRecordsEqual(current.patchStatus, initial.patchStatus)) count++
  if (!areBooleanRecordsEqual(current.fixConfidence, initial.fixConfidence)) count++
  if (!areBooleanRecordsEqual(current.scaClassification, initial.scaClassification)) count++
  if (!areBooleanRecordsEqual(current.severityUpdate, initial.severityUpdate)) count++
  if (
    !areBooleanRecordsEqual(current.severity.labels, initial.severity.labels) ||
    current.severity.minScore !== initial.severity.minScore ||
    current.severity.maxScore !== initial.severity.maxScore
  )
    count++
  if (
    !areBooleanRecordsEqual(current.suggestedSeverity.labels, initial.suggestedSeverity.labels) ||
    current.suggestedSeverity.minScore !== initial.suggestedSeverity.minScore ||
    current.suggestedSeverity.maxScore !== initial.suggestedSeverity.maxScore
  )
    count++
  return count
}

/**
 * Bumped whenever a persisted slice changes shape.
 *
 * Storage keys are stable across releases (`filter-state/${detector}/${quickViewId}`), so a
 * returning user rehydrates whatever the previous release wrote, and zustand's default merge is
 * shallow — a stored slice replaces the fresh one wholesale rather than being merged field by
 * field. Without a bump, last release's `suggestedSeverity` arrives with `minScore`/`maxScore`
 * `undefined` rather than `null`, which the `!==` comparisons in `getAdditionalFilterCount` and
 * `isInitialState` read as an active filter forever; worse, Pixee severity labels selected under
 * the old checkbox list keep filtering the table and the CSV export on a score-scale scan, where
 * the range branch renders no checkboxes to clear them with.
 *
 * v1: `suggestedSeverity` gained `minScore`/`maxScore`.
 */
const PERSISTED_FILTER_STATE_VERSION = 1

const partializeFilterState = (state: FilterStateStore): FilterStateStoreValues => ({
  hasFix: state.hasFix,
  hasTriage: state.hasTriage,
  hasScaAnalysis: state.hasScaAnalysis,
  filterToUnanalyzed: state.filterToUnanalyzed,
  filterToAnalyzed: state.filterToAnalyzed,
  filterToScaUnanalyzed: state.filterToScaUnanalyzed,
  classification: state.classification,
  fixResult: state.fixResult,
  patchStatus: state.patchStatus,
  fixConfidence: state.fixConfidence,
  scaClassification: state.scaClassification,
  severityUpdate: state.severityUpdate,
  severity: state.severity,
  suggestedSeverity: state.suggestedSeverity,
})

type CreateFilterStateStoreOptions = {
  initialState?: Partial<FilterStateStoreValues>
  storageKey?: string
}

const createFilterStateStore = ({
  initialState = DEFAULT_INITIAL_STATE,
  storageKey,
}: CreateFilterStateStoreOptions = {}) => {
  const fullInitialState = {
    ...DEFAULT_INITIAL_STATE,
    ...initialState,
  }

  const stateCreator: StateCreator<FilterStateStore> = (set, get) => ({
    ...fullInitialState,
    isInitialState: () => {
      const current = get()
      return (
        current.hasFix === fullInitialState.hasFix &&
        current.hasTriage === fullInitialState.hasTriage &&
        current.hasScaAnalysis === fullInitialState.hasScaAnalysis &&
        areBooleanRecordsEqual(current.classification, fullInitialState.classification) &&
        areBooleanRecordsEqual(current.fixResult, fullInitialState.fixResult) &&
        areBooleanRecordsEqual(current.patchStatus, fullInitialState.patchStatus) &&
        areBooleanRecordsEqual(current.fixConfidence, fullInitialState.fixConfidence) &&
        areBooleanRecordsEqual(current.scaClassification, fullInitialState.scaClassification) &&
        areBooleanRecordsEqual(current.severityUpdate, fullInitialState.severityUpdate) &&
        areBooleanRecordsEqual(current.severity.labels, fullInitialState.severity.labels) &&
        current.severity.minScore === fullInitialState.severity.minScore &&
        current.severity.maxScore === fullInitialState.severity.maxScore &&
        areBooleanRecordsEqual(current.suggestedSeverity.labels, fullInitialState.suggestedSeverity.labels) &&
        current.suggestedSeverity.minScore === fullInitialState.suggestedSeverity.minScore &&
        current.suggestedSeverity.maxScore === fullInitialState.suggestedSeverity.maxScore
      )
    },
    resetFilters: () => set(() => fullInitialState),
    toggleHasFix: () =>
      set(state =>
        produce(state, draft => {
          draft.hasFix = !draft.hasFix
        })
      ),
    toggleHasTriage: () =>
      set(state =>
        produce(state, draft => {
          draft.hasTriage = !draft.hasTriage
        })
      ),
    toggleHasScaAnalysis: () =>
      set(state =>
        produce(state, draft => {
          draft.hasScaAnalysis = !draft.hasScaAnalysis
        })
      ),
    toggleClassification: key =>
      set(state =>
        produce(state, draft => {
          draft.classification[key] = !draft.classification[key]
        })
      ),
    toggleClassificationGroup: () =>
      set(state =>
        produce(state, draft => {
          const isAnyOn = Object.values(draft.classification).some(value => value)
          draft.classification.true_positive = !isAnyOn
          draft.classification.suspicious = !isAnyOn
          draft.classification.false_positive = !isAnyOn
          draft.classification.wont_fix = !isAnyOn
          draft.classification.inconclusive = !isAnyOn
          draft.classification.blocked = !isAnyOn
          draft.classification.excluded = !isAnyOn
          draft.classification.error = !isAnyOn
          draft.classification.not_triaged = !isAnyOn
        })
      ),
    toggleFixResult: key =>
      set(state =>
        produce(state, draft => {
          draft.fixResult[key] = !draft.fixResult[key]
        })
      ),
    toggleFixResultGroup: () =>
      set(state =>
        produce(state, draft => {
          const isAnyOn =
            Object.values(draft.fixResult).some(value => value) || Object.values(draft.patchStatus).some(value => value)
          for (const k of Object.keys(draft.fixResult) as FilterableFixStatus[]) {
            draft.fixResult[k] = !isAnyOn
          }
          for (const k of Object.keys(draft.patchStatus) as FilterablePatchStatus[]) {
            draft.patchStatus[k] = !isAnyOn
          }
        })
      ),
    togglePatchStatus: key =>
      set(state =>
        produce(state, draft => {
          draft.patchStatus[key] = !draft.patchStatus[key]
        })
      ),
    togglePatchStatusGroup: () =>
      set(state =>
        produce(state, draft => {
          const isAnyOn = Object.values(draft.patchStatus).some(value => value)
          for (const k of Object.keys(draft.patchStatus) as FilterablePatchStatus[]) {
            draft.patchStatus[k] = !isAnyOn
          }
        })
      ),
    toggleFixConfidence: key =>
      set(state =>
        produce(state, draft => {
          draft.fixConfidence[key] = !draft.fixConfidence[key]
        })
      ),
    toggleFixConfidenceGroup: () =>
      set(state =>
        produce(state, draft => {
          const isAnyOn = Object.values(draft.fixConfidence).some(value => value)
          draft.fixConfidence.high = !isAnyOn
          draft.fixConfidence.medium = !isAnyOn
          draft.fixConfidence.low = !isAnyOn
          draft.fixConfidence['no-rating'] = !isAnyOn
        })
      ),
    toggleScaClassification: key =>
      set(state =>
        produce(state, draft => {
          draft.scaClassification[key] = !draft.scaClassification[key]
        })
      ),
    toggleScaClassificationGroup: () =>
      set(state =>
        produce(state, draft => {
          const isAnyOn = Object.values(draft.scaClassification).some(value => value)
          draft.scaClassification.exploitable = !isAnyOn
          draft.scaClassification['not-exploitable'] = !isAnyOn
          draft.scaClassification['potentially-exploitable'] = !isAnyOn
        })
      ),
    toggleNoFixNecessary: () =>
      set(state =>
        produce(state, draft => {
          const isAllOn = draft.classification.false_positive && draft.classification.wont_fix
          draft.classification.false_positive = !isAllOn
          draft.classification.wont_fix = !isAllOn
        })
      ),
    toggleFixResultBlocked: () =>
      set(state =>
        produce(state, draft => {
          const next = !draft.fixResult.blocked
          draft.fixResult.blocked = next
          if (next) {
            draft.classification.true_positive = true
            draft.classification.suspicious = true
            draft.classification.inconclusive = true
          }
        })
      ),
    toggleSeverityUpdate: key =>
      set(state =>
        produce(state, draft => {
          draft.severityUpdate[key] = !draft.severityUpdate[key]
        })
      ),
    toggleSeverityUpdateGroup: () =>
      set(state =>
        produce(state, draft => {
          const isAnyOn = Object.values(draft.severityUpdate).some(value => value)
          draft.severityUpdate.decreased = !isAnyOn
          draft.severityUpdate.increased = !isAnyOn
          draft.severityUpdate.no_update = !isAnyOn
        })
      ),
    toggleSeverityLabel: label =>
      set(state =>
        produce(state, draft => {
          if (draft.severity.labels[label]) {
            const { [label]: _, ...rest } = draft.severity.labels
            draft.severity.labels = rest
          } else {
            draft.severity.labels[label] = true
          }
        })
      ),
    toggleSeverityGroup: availableLabels =>
      set(state =>
        produce(state, draft => {
          draft.severity.labels = toggledLabelGroup(draft.severity.labels, availableLabels)
        })
      ),
    setSeverityMinScore: value =>
      set(state =>
        produce(state, draft => {
          draft.severity.minScore = value
        })
      ),
    setSeverityMaxScore: value =>
      set(state =>
        produce(state, draft => {
          draft.severity.maxScore = value
        })
      ),
    toggleSuggestedSeverityLabel: label =>
      set(state =>
        produce(state, draft => {
          if (draft.suggestedSeverity.labels[label]) {
            const { [label]: _, ...rest } = draft.suggestedSeverity.labels
            draft.suggestedSeverity.labels = rest
          } else {
            draft.suggestedSeverity.labels[label] = true
          }
        })
      ),
    toggleSuggestedSeverityGroup: availableLabels =>
      set(state =>
        produce(state, draft => {
          draft.suggestedSeverity.labels = toggledLabelGroup(draft.suggestedSeverity.labels, availableLabels)
        })
      ),
    setSuggestedSeverityMinScore: value =>
      set(state =>
        produce(state, draft => {
          draft.suggestedSeverity.minScore = value
        })
      ),
    setSuggestedSeverityMaxScore: value =>
      set(state =>
        produce(state, draft => {
          draft.suggestedSeverity.maxScore = value
        })
      ),
    toggleAnalyzed: () =>
      set(state =>
        produce(state, draft => {
          draft.filterToAnalyzed = !draft.filterToAnalyzed
        })
      ),
  })

  if (storageKey) {
    return createStore<FilterStateStore>()(
      persist(stateCreator, {
        name: storageKey,
        version: PERSISTED_FILTER_STATE_VERSION,
        partialize: partializeFilterState,
        // Only ever called for a stored version other than the current one, so the sole migration
        // is v0 → v1: drop the whole slice back to its initial state rather than patch it, since a
        // v0 payload can carry label selections the new UI has no affordance to clear.
        //
        // The rest of the payload is carried across as written. It is only asserted to be the shape
        // it was persisted in, never parsed, so a hand-edited or half-written entry can put a wrong
        // value in a filter the store then shows as active — recoverable with Reset filters, and
        // the cost of not maintaining a second, schema-shaped spelling of these values.
        migrate: persisted => ({
          ...(persisted as Partial<FilterStateStoreValues>),
          suggestedSeverity: fullInitialState.suggestedSeverity,
        }),
      })
    )
  }

  return createStore<FilterStateStore>(stateCreator)
}

const FilterStateStoreContext = createContext<ReturnType<typeof createFilterStateStore> | null>(null)

export const FilterStateStoreProvider = ({
  children,
  initialState,
  storageKey,
}: React.PropsWithChildren<{
  initialState?: Partial<FilterStateStoreValues>
  storageKey?: string
}>) => {
  const [store, setStore] = useState(() => createFilterStateStore({ initialState, storageKey }))
  const prevStorageKeyRef = useRef(storageKey)

  // When storageKey becomes available (e.g. detector loads), recreate store with persist
  // while preserving the current filter state to avoid losing in-flight changes.
  useEffect(() => {
    if (storageKey && !prevStorageKeyRef.current) {
      const currentState = partializeFilterState(store.getState())
      setStore(createFilterStateStore({ initialState: currentState, storageKey }))
    }
    prevStorageKeyRef.current = storageKey
  }, [storageKey, store])

  return <FilterStateStoreContext value={store}>{children}</FilterStateStoreContext>
}

export function useFilterStateStore<U>(selector: (state: FilterStateStore) => U) {
  const store = useContext(FilterStateStoreContext)

  if (!store) {
    throw new Error('useFilterStateStore must be used within a FilterStateStoreProvider')
  }

  return useStore(store, selector)
}

export function useGetScanFindingsWithFilters({
  pagination = { pageIndex: 0, pageSize: 10 },
  scanId,
  filterState,
  search,
  sort,
  order,
  embed,
  refetchInterval,
}: {
  pagination?: Pagination
  scanId?: string
  filterState: FilterStateStoreValues
  search?: string
  sort?: FindingSortField
  order?: SortOrder
  embed?: FindingEmbedParameter[]
  refetchInterval?:
    number | false | ((query: { state: { data: FindingAggregateResponse | undefined } }) => number | false | undefined)
}) {
  return useGetScanFindings({
    scanId,
    pagination,
    search,
    sort,
    order,
    embed,
    refetchInterval,
    ...mapFilterStateToGetScanFindingsParams(filterState),
  })
}

/**
 * Fires per-category queries to get statistics with each category's filters zeroed out.
 * This allows intra-category counts to remain unfiltered while cross-category counts
 * reflect active filters.
 *
 * Only fires queries for categories that have active filters — if a category has no
 * active filters, its statistics are identical to the main query's statistics.
 */
export function usePerCategoryStatistics({
  scanId,
  filterState,
  search,
  sort,
  order,
  visibleCategories,
}: {
  scanId?: string
  filterState: FilterStateStoreValues
  search?: string
  sort?: FindingSortField
  order?: SortOrder
  visibleCategories: FilterCategory[]
}) {
  const classificationActive =
    visibleCategories.includes('classification') && categoryHasActiveFilters(filterState, 'classification')
  const classificationFilterState = useMemo(
    () => (classificationActive ? createFilterStateWithoutCategory(filterState, 'classification') : filterState),
    [classificationActive, filterState]
  )
  const classificationQuery = useGetScanFindings({
    scanId,
    pagination: { pageIndex: 0, pageSize: 1 },
    search,
    sort,
    order,
    enabled: classificationActive,
    ...mapFilterStateToGetScanFindingsParams(classificationFilterState),
  })

  const fixResultActive = visibleCategories.includes('fixResult') && categoryHasActiveFilters(filterState, 'fixResult')
  const fixResultFilterState = useMemo(
    () => (fixResultActive ? createFilterStateWithoutCategory(filterState, 'fixResult') : filterState),
    [fixResultActive, filterState]
  )
  const fixResultQuery = useGetScanFindings({
    scanId,
    pagination: { pageIndex: 0, pageSize: 1 },
    search,
    sort,
    order,
    enabled: fixResultActive,
    ...mapFilterStateToGetScanFindingsParams(fixResultFilterState),
  })

  const fixConfidenceActive =
    visibleCategories.includes('fixConfidence') && categoryHasActiveFilters(filterState, 'fixConfidence')
  const fixConfidenceFilterState = useMemo(
    () => (fixConfidenceActive ? createFilterStateWithoutCategory(filterState, 'fixConfidence') : filterState),
    [fixConfidenceActive, filterState]
  )
  const fixConfidenceQuery = useGetScanFindings({
    scanId,
    pagination: { pageIndex: 0, pageSize: 1 },
    search,
    sort,
    order,
    enabled: fixConfidenceActive,
    ...mapFilterStateToGetScanFindingsParams(fixConfidenceFilterState),
  })

  const scaClassificationActive =
    visibleCategories.includes('scaClassification') && categoryHasActiveFilters(filterState, 'scaClassification')
  const scaClassificationFilterState = useMemo(
    () => (scaClassificationActive ? createFilterStateWithoutCategory(filterState, 'scaClassification') : filterState),
    [scaClassificationActive, filterState]
  )
  const scaClassificationQuery = useGetScanFindings({
    scanId,
    pagination: { pageIndex: 0, pageSize: 1 },
    search,
    sort,
    order,
    enabled: scaClassificationActive,
    ...mapFilterStateToGetScanFindingsParams(scaClassificationFilterState),
  })

  return useMemo(() => {
    const result: PerCategoryStatistics = {}
    if (classificationActive && classificationQuery.data) result.classification = classificationQuery.data
    if (fixResultActive && fixResultQuery.data) result.fixResult = fixResultQuery.data
    if (fixConfidenceActive && fixConfidenceQuery.data) result.fixConfidence = fixConfidenceQuery.data
    if (scaClassificationActive && scaClassificationQuery.data) result.scaClassification = scaClassificationQuery.data
    return Object.keys(result).length > 0 ? result : undefined
  }, [
    classificationActive,
    classificationQuery.data,
    fixResultActive,
    fixResultQuery.data,
    fixConfidenceActive,
    fixConfidenceQuery.data,
    scaClassificationActive,
    scaClassificationQuery.data,
  ])
}

/**
 * Projects the filter state onto the findings filters.
 *
 * The result is spread into `useGetScanFindings` and into `useExportScanFindingsCsv`, and excess
 * properties are not checked through a spread — so a name that drifts from what those two accept
 * would compile cleanly and silently drop the filter from the table and from the CSV export. The
 * `satisfies ScanFindingsFilters` below is what turns that into a type error, and both consumers
 * take their filter names from that one type.
 */
export const mapFilterStateToGetScanFindingsParams = (filterState: FilterStateStoreValues) => {
  const selectedClassifications = Object.entries(filterState.classification)
    .filter(([_, v]) => v)
    .map(([k]) => k as FilterableClassification)
  const selectedFixResultStatuses = Object.entries(filterState.fixResult)
    .filter(([_, v]) => v)
    .map(([k]) => k as FilterableFixStatus)
  const selectedPatchStatuses = Object.entries(filterState.patchStatus)
    .filter(([_, v]) => v)
    .map(([k]) => k as FilterablePatchStatus)
  const selectedFixConfidences = Object.entries(filterState.fixConfidence)
    .filter(([_, v]) => v)
    .map(([k]) => k as FixConfidence)
  const selectedScaClassifications = Object.entries(filterState.scaClassification)
    .filter(([_, v]) => v)
    .map(([k]) => k as ScaClassificationResponse)
  const selectedSeverityUpdates = Object.entries(filterState.severityUpdate)
    .filter(([_, v]) => v)
    .map(([k]) => SeverityUpdateFilterSchema.parse(k))
  const selectedSeverityLabels = Object.entries(filterState.severity.labels)
    .filter(([_, v]) => v)
    .map(([k]) => k)
  const selectedSuggestedSeverityLabels = Object.entries(filterState.suggestedSeverity.labels)
    .filter(([_, v]) => v)
    .map(([k]) => k)

  // Map hasFix to fix-status filter (completed = has actionable recommendations)
  // Combine with fixResult selections
  const fixStatusesFromHasFix = filterState.hasFix ? ['completed'] : []
  const allFixStatuses = [...new Set([...fixStatusesFromHasFix, ...selectedFixResultStatuses])]
  const fixStatuses = allFixStatuses.length > 0 ? allFixStatuses : undefined

  // Map hasTriage to triage-status filter (completed = has actionable recommendations)
  const triageStatuses = filterState.hasTriage ? ['completed'] : undefined

  // Map filterToScaUnanalyzed to sca-status filter (not-analyzed = no SCA analysis)
  const scaStatus = filterState.filterToScaUnanalyzed ? ('not-analyzed' as const) : undefined

  // Map analysis filters: filterToAnalyzed sends true, filterToUnanalyzed sends false
  const analyzed = filterState.filterToAnalyzed ? true : filterState.filterToUnanalyzed ? false : undefined

  return {
    fixStatuses,
    triageStatuses,
    triageSuggestedStatuses: selectedClassifications.length > 0 ? selectedClassifications : undefined,
    patchStatuses: selectedPatchStatuses.length > 0 ? selectedPatchStatuses : undefined,
    includeSeverityLabels: selectedSeverityLabels.length > 0 ? selectedSeverityLabels : undefined,
    includeSuggestedSeverityLabels:
      selectedSuggestedSeverityLabels.length > 0 ? selectedSuggestedSeverityLabels : undefined,
    minimumSeverityScore: filterState.severity.minScore ?? undefined,
    maximumSeverityScore: filterState.severity.maxScore ?? undefined,
    minimumSuggestedSeverityScore: filterState.suggestedSeverity.minScore ?? undefined,
    maximumSuggestedSeverityScore: filterState.suggestedSeverity.maxScore ?? undefined,
    scaStatus,
    analyzed,
    fixConfidences: selectedFixConfidences.length > 0 ? selectedFixConfidences : undefined,
    scaClassifications: selectedScaClassifications.length > 0 ? selectedScaClassifications : undefined,
    severityUpdates: selectedSeverityUpdates.length > 0 ? selectedSeverityUpdates : undefined,
  } satisfies ScanFindingsFilters
}
